import { unzipSync } from "fflate";

const STEM_MIME_TYPE = "audio/wav";
const STEM_ORDER = ["vocals", "drums", "bass", "other"] as const;

export interface ExtractedStem {
  blob: Blob;
  filename: string;
  key: string;
  label: string;
  mimeType: string;
}

function stemSortValue(stemKey: string): number {
  const normalizedKey = stemKey.toLowerCase();
  const index = STEM_ORDER.indexOf(
    normalizedKey as (typeof STEM_ORDER)[number],
  );

  return index === -1 ? STEM_ORDER.length : index;
}

function getStemKey(filename: string): string {
  return filename.replace(/\.[^/.]+$/, "").toLowerCase();
}

function getStemLabel(stemKey: string): string {
  return stemKey.charAt(0).toUpperCase() + stemKey.slice(1);
}

export async function extractStemArchive(
  archiveBlob: Blob,
): Promise<ExtractedStem[]> {
  const archiveBuffer = await archiveBlob.arrayBuffer();
  const archiveEntries = unzipSync(new Uint8Array(archiveBuffer));

  return Object.entries(archiveEntries)
    .filter(([filename]) => {
      const lower = filename.toLowerCase();
      return lower.endsWith(".wav") || lower.endsWith(".mp3");
    })
    .sort(([filenameA], [filenameB]) => {
      const stemOrderDelta =
        stemSortValue(getStemKey(filenameA)) -
        stemSortValue(getStemKey(filenameB));

      if (stemOrderDelta !== 0) {
        return stemOrderDelta;
      }

      return filenameA.localeCompare(filenameB);
    })
    .map(([filename, stemBytes]) => {
      const key = getStemKey(filename);
      const mimeType = filename.toLowerCase().endsWith(".mp3")
        ? "audio/mpeg"
        : STEM_MIME_TYPE;

      return {
        blob: new Blob([stemBytes.slice()], { type: mimeType }),
        filename,
        key,
        label: getStemLabel(key),
        mimeType,
      };
    });
}

